/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ShadowMCPAccessSummary,
  ShadowMCPAccessSummary$inboundSchema,
} from "./shadowmcpaccesssummary.js";
import {
  ShadowMCPInventoryApprovalRequest,
  ShadowMCPInventoryApprovalRequest$inboundSchema,
} from "./shadowmcpinventoryapprovalrequest.js";
import {
  ShadowMCPInventoryRequestSummary,
  ShadowMCPInventoryRequestSummary$inboundSchema,
} from "./shadowmcpinventoryrequestsummary.js";

/**
 * Deprecated: read access_summary.state. Kept one release so older clients keep rendering, then removed together with making access_summary required. Note the values themselves are corrected in this release: URLs whose bypass grants cover only part of a policy's audience now read restricted where they previously read allowed.
 */
export const Access = {
  None: "none",
  Allowed: "allowed",
  Blocked: "blocked",
  Restricted: "restricted",
} as const;
/**
 * Deprecated: read access_summary.state. Kept one release so older clients keep rendering, then removed together with making access_summary required. Note the values themselves are corrected in this release: URLs whose bypass grants cover only part of a policy's audience now read restricted where they previously read allowed.
 */
export type Access = OpenEnum<typeof Access>;

/**
 * What the row identifies: a server URL observed or requested, or a local stdio command known only through its review. Absent means server_url.
 */
export const TargetKind = {
  ServerUrl: "server_url",
  StdioCommand: "stdio_command",
} as const;
/**
 * What the row identifies: a server URL observed or requested, or a local stdio command known only through its review. Absent means server_url.
 */
export type TargetKind = OpenEnum<typeof TargetKind>;

export type ShadowMCPInventoryServer = {
  /**
   * Deprecated: read access_summary.state. Kept one release so older clients keep rendering, then removed together with making access_summary required. Note the values themselves are corrected in this release: URLs whose bypass grants cover only part of a policy's audience now read restricted where they previously read allowed.
   */
  access: Access;
  /**
   * The enforcement verdict for a shadow MCP server, computed server-side from policies, grants, and the recorded decision. state is the canonical compression of who may call the server; the remaining fields name the mechanisms so a client renders wording without re-deriving enforcement.
   */
  accessSummary?: ShadowMCPAccessSummary | undefined;
  allowedPolicyIds: Array<string>;
  /**
   * The MCP approval request tracking review status for a server. Status records the review outcome, which may cover only selected principals; the server's access field reports enforcement state.
   */
  approvalRequest?: ShadowMCPInventoryApprovalRequest | undefined;
  /**
   * Enabled blocking policies that block this server via a risk_policy:block grant (allow_all policies only).
   */
  blockedPolicyIds: Array<string>;
  canonicalServerUrl: string;
  firstSeen: Date;
  lastCalled?: Date | undefined;
  lastSeen: Date;
  latestRequest?: ShadowMCPInventoryRequestSummary | undefined;
  observedUseCount: number;
  requestCount: number;
  serverName?: string | undefined;
  serverSlug: string;
  /**
   * What the row identifies: a server URL observed or requested, or a local stdio command known only through its review. Absent means server_url.
   */
  targetKind?: TargetKind | undefined;
  /**
   * The users who reached this server most.
   */
  topUsers: Array<string>;
  urlHost: string;
  /**
   * Distinct users who reached this server.
   */
  userCount: number;
};

/** @internal */
export const Access$inboundSchema: z.ZodMiniType<Access, unknown> = openEnums
  .inboundSchema(Access);

/** @internal */
export const TargetKind$inboundSchema: z.ZodMiniType<TargetKind, unknown> =
  openEnums.inboundSchema(TargetKind);

/** @internal */
export const ShadowMCPInventoryServer$inboundSchema: z.ZodMiniType<
  ShadowMCPInventoryServer,
  unknown
> = z.pipe(
  z.object({
    access: Access$inboundSchema,
    access_summary: z.optional(ShadowMCPAccessSummary$inboundSchema),
    allowed_policy_ids: z.array(z.string()),
    approval_request: z.optional(
      ShadowMCPInventoryApprovalRequest$inboundSchema,
    ),
    blocked_policy_ids: z.array(z.string()),
    canonical_server_url: z.string(),
    first_seen: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    last_called: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_seen: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    latest_request: z.optional(ShadowMCPInventoryRequestSummary$inboundSchema),
    observed_use_count: z.int(),
    request_count: z.int(),
    server_name: z.optional(z.string()),
    server_slug: z.string(),
    target_kind: z.optional(TargetKind$inboundSchema),
    top_users: z.array(z.string()),
    url_host: z.string(),
    user_count: z.int(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "access_summary": "accessSummary",
      "allowed_policy_ids": "allowedPolicyIds",
      "approval_request": "approvalRequest",
      "blocked_policy_ids": "blockedPolicyIds",
      "canonical_server_url": "canonicalServerUrl",
      "first_seen": "firstSeen",
      "last_called": "lastCalled",
      "last_seen": "lastSeen",
      "latest_request": "latestRequest",
      "observed_use_count": "observedUseCount",
      "request_count": "requestCount",
      "server_name": "serverName",
      "server_slug": "serverSlug",
      "target_kind": "targetKind",
      "top_users": "topUsers",
      "url_host": "urlHost",
      "user_count": "userCount",
    });
  }),
);

export function shadowMCPInventoryServerFromJSON(
  jsonString: string,
): SafeParseResult<ShadowMCPInventoryServer, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ShadowMCPInventoryServer$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ShadowMCPInventoryServer' from JSON`,
  );
}
